import { getObjectColors } from '../../../utils/objectColors';
import { getServiceIcon } from '../../../utils/serviceIcons';

function formatDurationHours(hours: number): string {
  const v = Math.round((hours + Number.EPSILON) * 100) / 100;
  return `${v.toFixed(2)}h`;
}

interface TaskBlockProps {
  task: {
    id: string;
    taskName: string;
    objectId?: string;
    objectName: string;
    objectShortName?: string;
    serviceName: string;
    startTime: string;
    endTime: string;
    durationHours: number;
  };
  isDragging?: boolean;
  onDragStart?: (taskId: string) => void;
  onDragEnd?: () => void;
  onClick?: () => void;
}

export function TaskBlock({ task, isDragging, onDragStart, onDragEnd, onClick }: TaskBlockProps) {
  const { color, soft, ink } = getObjectColors(task.objectId ?? task.objectName);
  const ServiceIcon = getServiceIcon(task.serviceName);
  const displayName = task.objectShortName || task.objectName;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.();
  };

  return (
    <button
      type="button"
      draggable={!!onDragStart}
      onDragStart={() => onDragStart?.(task.id)}
      onDragEnd={onDragEnd}
      onClick={handleClick}
      aria-label={`${task.objectName} ${task.startTime}–${task.endTime}`}
      title={task.objectName}
      className={`
        relative block w-full text-left rounded-md px-1.5 py-1 min-w-0 overflow-hidden transition-shadow
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
        ${onDragStart || onClick ? 'cursor-pointer hover:shadow-sm' : ''}
        ${isDragging ? 'opacity-50' : 'opacity-100'}
      `}
      style={{
        background: soft,
        borderLeft: `3px solid ${color}`,
        color: ink,
      }}
    >
      <div className="flex items-center justify-between gap-1 min-w-0">
        <span className="text-[10px] font-medium text-tabular truncate">
          {task.startTime}–{task.endTime}
        </span>
        <span className="text-[10px] font-semibold text-tabular shrink-0">
          {formatDurationHours(task.durationHours)}
        </span>
      </div>
      <div className="text-[11px] font-semibold truncate leading-tight">
        {displayName}
      </div>
      <div
        className="flex items-center gap-1 text-[10px] truncate"
        style={{ color }}
        title={task.serviceName}
      >
        <ServiceIcon size={10} strokeWidth={2.25} className="shrink-0" />
        <span className="truncate">{task.serviceName}</span>
      </div>
    </button>
  );
}
